import { useState } from 'react';
import { Icon, Button, Badge } from '@/components/atoms';
import { Input } from '@kisanlink/ui-commons';
import { Modal } from '@/components/molecules';
import {
  useCategoriesList,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useSeedCategoriesMutation,
} from '@/hooks/useErpApi';

interface Category {
  id: string;
  name: string;
  description?: string;
  subcategories?: Array<{
    id: string;
    name: string;
    description?: string;
  }>;
}

interface CategoryFormData {
  name: string;
  description: string;
}

export default function CategoriesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSeedModalOpen, setIsSeedModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    description: '',
  });
  const [error, setError] = useState<string | null>(null);

  const { data: categoriesData, isLoading, error: queryError, refetch } = useCategoriesList();
  const { mutate: createCategory, isLoading: createLoading } = useCreateCategoryMutation();
  const { mutate: updateCategory, isLoading: updateLoading } = useUpdateCategoryMutation();
  const { mutate: deleteCategory, isLoading: deleteLoading } = useDeleteCategoryMutation();
  const { mutate: seedCategories, isLoading: seedLoading } = useSeedCategoriesMutation();

  // Extract categories from API response
  // The API returns { data: [...], pagination: {...}, ... }
  // The useQuery hook extracts response.data, so categoriesData should be the array
  // But handle both cases: if it's an array use it, if it has a data property use that
  let categories: Category[] = [];
  if (categoriesData) {
    if (Array.isArray(categoriesData)) {
      categories = categoriesData;
    } else if ((categoriesData as any)?.data && Array.isArray((categoriesData as any).data)) {
      categories = (categoriesData as any).data;
    }
  }

  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    category.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAddCategory = () => {
    setFormData({ name: '', description: '' });
    setSelectedCategory(null);
    setError(null);
    setIsAddModalOpen(true);
  };

  const handleEditCategory = (category: Category) => {
    setFormData({
      name: category.name,
      description: category.description || '',
    });
    setSelectedCategory(category);
    setError(null);
    setIsEditModalOpen(true);
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!window.confirm('Are you sure you want to delete this category? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteCategory(categoryId);
      refetch();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete category');
    }
  };

  const handleSeedCategories = async () => {
    try {
      await seedCategories(undefined as any);
      alert('Categories seeded successfully!');
      setIsSeedModalOpen(false);
      refetch();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to seed categories');
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Category name is required');
      return;
    }

    try {
      if (selectedCategory) {
        await updateCategory({
          id: selectedCategory.id,
          data: {
            name: formData.name.trim(),
            description: formData.description.trim() || undefined,
          },
        });
        setIsEditModalOpen(false);
      } else {
        await createCategory({
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
        });
        setIsAddModalOpen(false);
      }
      setFormData({ name: '', description: '' });
      setSelectedCategory(null);
      refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save category');
    }
  };

  const handleFormCancel = () => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setFormData({ name: '', description: '' });
    setSelectedCategory(null);
    setError(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-neutral-600">Loading categories...</div>
      </div>
    );
  }

  if (queryError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-error-600">Error loading categories: {queryError.message}</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Categories</h1>
          <p className="text-neutral-600 mt-1">Manage product categories and subcategories</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setIsSeedModalOpen(true)}>
            <Icon name="Database" size={16} className="mr-2" />
            Seed Categories
          </Button>
          <Button variant="primary" onClick={handleAddCategory}>
            <Icon name="Plus" size={16} className="mr-2" />
            Add Category
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4">
        <Input
          type="text"
          placeholder="Search categories..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full"
        />
      </div>

      {/* Categories List */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
        <div className="p-6">
          {filteredCategories.length === 0 ? (
            <div className="text-center py-12 text-neutral-500">
              <Icon name="Folder" size={48} className="mx-auto mb-4 text-neutral-400" />
              <p className="text-lg font-medium mb-2">No categories found</p>
              <p className="text-sm">
                {searchQuery ? 'Try adjusting your search' : 'Get started by creating your first category'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredCategories.map((category) => (
                <div
                  key={category.id}
                  className="border border-neutral-200 rounded-lg p-4 hover:bg-neutral-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-neutral-900">{category.name}</h3>
                        {category.subcategories && category.subcategories.length > 0 && (
                          <Badge variant="info">
                            {category.subcategories.length} subcategor{category.subcategories.length === 1 ? 'y' : 'ies'}
                          </Badge>
                        )}
                      </div>
                      {category.description && (
                        <p className="text-sm text-neutral-600 mb-3">{category.description}</p>
                      )}
                      {category.subcategories && category.subcategories.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs font-medium text-neutral-500 mb-2">Subcategories:</p>
                          <div className="flex flex-wrap gap-2">
                            {category.subcategories.map((subcat) => (
                              <Badge key={subcat.id} variant="secondary" className="text-xs">
                                {subcat.name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleEditCategory(category)}
                        disabled={deleteLoading}
                      >
                        <Icon name="Pencil" size={16} className="mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="error"
                        size="sm"
                        onClick={() => handleDeleteCategory(category.id)}
                        disabled={deleteLoading}
                      >
                        <Icon name="Trash2" size={16} className="mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Category Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={handleFormCancel}
        title="Add Category"
        size="md"
      >
        <form onSubmit={handleFormSubmit} className="space-y-4">
          {error && (
            <div className="bg-error-50 border border-error-200 rounded-lg p-3">
              <p className="text-error-700 text-sm">{error}</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Category Name *
            </label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., FERTILIZERS"
              required
              className="w-full"
            />
            <p className="text-xs text-neutral-500 mt-1">
              Name will be automatically normalized to UPPER_SNAKE_CASE
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Category description..."
              rows={3}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="flex gap-2 justify-end pt-4 border-t border-neutral-200">
            <Button type="button" variant="secondary" onClick={handleFormCancel}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={createLoading}>
              {createLoading ? 'Creating...' : 'Create Category'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Category Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={handleFormCancel}
        title="Edit Category"
        size="md"
      >
        <form onSubmit={handleFormSubmit} className="space-y-4">
          {error && (
            <div className="bg-error-50 border border-error-200 rounded-lg p-3">
              <p className="text-error-700 text-sm">{error}</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Category Name *
            </label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., FERTILIZERS"
              required
              className="w-full"
            />
            <p className="text-xs text-neutral-500 mt-1">
              Name will be automatically normalized to UPPER_SNAKE_CASE
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Category description..."
              rows={3}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="flex gap-2 justify-end pt-4 border-t border-neutral-200">
            <Button type="button" variant="secondary" onClick={handleFormCancel}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={updateLoading}>
              {updateLoading ? 'Updating...' : 'Update Category'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Seed Categories Modal */}
      <Modal
        isOpen={isSeedModalOpen}
        onClose={() => setIsSeedModalOpen(false)}
        title="Seed Predefined Categories"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-neutral-600">
            This will create predefined categories and subcategories if they don't already exist.
            The operation is idempotent - it won't create duplicates.
          </p>
          <div className="bg-neutral-50 rounded-lg p-4">
            <p className="text-sm font-medium text-neutral-900 mb-2">Predefined Categories:</p>
            <ul className="text-sm text-neutral-600 space-y-1 list-disc list-inside">
              <li>SEEDS</li>
              <li>FERTILIZERS (with subcategories: BULK, WATER_SOLUBLE, MICRONUTRIENTS, MACRONUTRIENTS)</li>
              <li>PESTICIDES (with subcategories: WEEDICIDES, INSECTICIDES, FUNGICIDES, ORGANIC)</li>
              <li>BIO_PRODUCTS (with subcategories: BULK, LIQUIDS, OTHER)</li>
              <li>IMPLEMENTS (with subcategories: WEEDING, SOWING, SPRAYERS)</li>
              <li>IRRIGATION (with subcategories: PIPES, DRIPPERS, SPRINKLERS, AUTOMATION_MACHINES, OTHER)</li>
              <li>OTHER</li>
            </ul>
          </div>
          <div className="flex gap-2 justify-end pt-4 border-t border-neutral-200">
            <Button type="button" variant="secondary" onClick={() => setIsSeedModalOpen(false)}>
              Cancel
            </Button>
            <Button type="button" variant="primary" onClick={handleSeedCategories} disabled={seedLoading}>
              {seedLoading ? 'Seeding...' : 'Seed Categories'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

